import { css } from '@emotion/react';
import { ReactNode } from 'react';

import { SasIconButton } from './SasIcon';
import { theme } from './theme';

const styles = {
  backdrop: (backDrop: boolean | undefined) => css`
    position: fixed;
    inset: 0;
    padding: 0;
    margin: 0;
    background: ${backDrop ? 'var(--background-overlay)' : 'transparent'};
    display: block;
    width: 100%;
    opacity: 0.5;
    z-index: 3;
  `,
  wrapper: (width: number, tipPosition: Position, backDrop: boolean, placement: Placement) => css`
    position: absolute;
    background: var(--color-white);
    filter: drop-shadow(0px 0 7px var(--color-gray-3));
    min-width: ${width}px;
    z-index: 10;
    top: ${placement.top};
    right: ${placement.right};
    left: ${placement.left};
    bottom: ${placement.bottom};
    border-radius: ${theme.borders.radius.sm};

    &:before {
      border: solid transparent;
      content: ' ';
      height: 0;
      width: 0;
      position: absolute;
      border-bottom-color: var(--color-white);
      border-width: 16px;
      left: ${tipPosition === 'topLeft' || tipPosition === 'bottomLeft' ? '-15px' : 'auto'};
      right: ${tipPosition === 'bottomRight' || tipPosition === 'topRight' ? '0' : 'auto'};
      bottom: ${tipPosition === 'bottomRight' || tipPosition === 'bottomLeft' ? '-15px' : 'auto'};
      top: ${tipPosition === 'topRight' || tipPosition === 'topLeft' ? '0px' : 'auto'};
      transform: ${tipPosition === 'topRight' || tipPosition === 'topLeft' || tipPosition === 'bottomLeft'
        ? 'rotate(180deg)'
        : 'rotate(90deg)'};
    }

    @media (max-width: ${theme.breakpoints.tablet}) {
      width: 100%;
      height: 100%;
      position: ${backDrop ? 'fixed' : 'absolute'};
      inset: 0;
    }
  `,
  header: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid var(--color-blue-1-3);
    padding: 12px 12px 8px 15px;
  `,
  title: css`
    margin: 0;
  `,
  children: css`
    padding: ${theme.spacing.md};
  `,
};

type Position = 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';

type Placement = {
  top?: string;
  bottom?: string;
  right?: string;
  left?: string;
};

type ToolTipProps = {
  tipPosition: Position;
  children: ReactNode;
  width: number;
  open: boolean;
  backDrop: boolean;
  closeOnClickOutside?: boolean;
  placement?: Placement;
  heading?: string;
  handleClose: (e: React.MouseEvent<HTMLElement>) => void;
};

export const ToolTip = ({
  tipPosition,
  heading,
  backDrop,
  children,
  width,
  placement = {
    top: 'auto',
    bottom: 'auto',
    right: 'auto',
    left: 'auto',
  },
  open,
  handleClose,
}: ToolTipProps) => {
  const handleOnClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    handleClose(e);
  };

  if (!open) return null;

  const hasHeading = Boolean(heading);

  return (
    <>
      <div css={styles.backdrop(backDrop)} onClick={handleOnClick} />

      <div
        css={styles.wrapper(width, tipPosition, backDrop, placement)}
        onClick={e => {
          e.stopPropagation();
        }}
      >
        {hasHeading && (
          <div css={styles.header}>
            <h4 css={styles.title}>{heading}</h4>
            <SasIconButton color="var(--g5-color)" name="cl--close" onClick={handleOnClick} />
          </div>
        )}
        <div css={!hasHeading && styles.children}>{children}</div>
      </div>
    </>
  );
};
